import { useEffect, useState } from 'react';
import { Card, CardContent, Grid, Stack, Typography, Box } from '@mui/material';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import DynamicFeedIcon from '@mui/icons-material/DynamicFeed';
import MemoryIcon from '@mui/icons-material/Memory';
import MessageIcon from '@mui/icons-material/Message';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import StorageIcon from '@mui/icons-material/Storage';
import TimelineIcon from '@mui/icons-material/Timeline';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import { useApiContext } from '@/contexts/apiContext';
import { useTranslation } from 'react-i18next';
import ErrorMessage from '@/components/errorMessage';
import EntityLlmUsages from '@/components/entities/administration/usageStats/llmUsages';

const StatCard = ({ title, value, icon, subtitle }: any) => (
  <Card
    sx={{
      p: 1.5,
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      justifyContent: 'flex-start',
      boxShadow: 'none',
      borderRadius: '12px',
      border: 1,
      borderColor: 'divider',
      bgcolor: 'background.paper',
    }}
  >
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
      <Box
        sx={{
          bgcolor: 'secondary.light',
          p: 0.75,
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '& .MuiSvgIcon-root': { fontSize: 18, color: 'text.primary' },
        }}
      >
        {icon}
      </Box>
      <Typography
        variant="caption"
        sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}
      >
        {title}
      </Typography>
    </Stack>
    <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary', mb: subtitle ? 0.25 : 0, lineHeight: 1 }}>
      {value}
    </Typography>
    {subtitle && (
      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
        {subtitle}
      </Typography>
    )}
  </Card>
);

type Props = {
  entity: any;
  maxHeight?: number;
};

const UserAppUsageStats = ({ entity, maxHeight }: Props) => {
  const { t } = useTranslation();
  const apiContext = useApiContext();

  const [appStats, setAppStats] = useState<any>();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<any>();

  useEffect(() => {
    const getUserAppUsageStats = async () => {
      setError(null);
      try {
        setLoading(true);
        const appStats = await apiContext.getUserAppInstanceUsageStats(entity.userAppDefId, entity.id);
        setAppStats(appStats);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    if (entity) {
      getUserAppUsageStats();
    }
  }, [entity]);

  return (
    <Stack
      justifyContent="flex-start"
      spacing={2}
      sx={{ width: '100%', height: maxHeight ? `${maxHeight}px` : '100%', overflowY: 'auto', overflowX: 'hidden' }}
    >
      {error && <ErrorMessage error={error} />}

      <Box sx={{ px: 0.5 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <StatCard title="Total Users" value={appStats?.users || 0} icon={<PeopleOutlineIcon />} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <StatCard title="Data Sources" value={appStats?.dataSources || 0} icon={<StorageIcon />} />
          </Grid>
          <Grid size={{ xs: 12, sm: 12, md: 4 }}>
            <StatCard title="Source Agents" value={appStats?.sourceAgents || 0} icon={<MemoryIcon />} />
          </Grid>

          {['generalQnA', 'documentQnA', 'studyAssistant', 'researchAssistant', 'selfServiceTroubleshooter'].includes(entity?.userAppDefId) && (
            <>
              <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                <StatCard title="Conversations" value={appStats?.conversations || 0} icon={<MessageIcon />} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                <StatCard
                  title="Interactions"
                  value={appStats?.interactions?.interactions || 0}
                  icon={<DynamicFeedIcon />}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                <StatCard
                  title="Valid Responses"
                  value={appStats?.interactions?.validResponses || 0}
                  icon={<CheckCircleOutlineIcon />}
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 3, md: 2.4 }}>
                <StatCard
                  title="Positive Feedback"
                  value={appStats?.interactions?.positiveFeedback || 0}
                  icon={<ThumbUpOutlinedIcon />}
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 3, md: 2.4 }}>
                <StatCard
                  title="Negative Feedback"
                  value={appStats?.interactions?.negativeFeedback || 0}
                  icon={<ThumbDownOutlinedIcon />}
                />
              </Grid>
            </>
          )}

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <StatCard title="Total Items" value={appStats?.dataSourceItemStats?.total || 0} icon={<StorageIcon />} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <StatCard
              title="Processed"
              value={appStats?.dataSourceItemStats?.processed || 0}
              icon={<CheckCircleOutlineIcon />}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <StatCard
              title="Unprocessed"
              value={appStats?.dataSourceItemStats?.unprocessed || 0}
              icon={<TimelineIcon />}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <StatCard
              title="Failed"
              value={appStats?.dataSourceItemStats?.number || 0}
              icon={<ErrorOutlineIcon />}
              subtitle={
                appStats?.dataSourceItemStats?.lastProcessed
                  ? `Last: ${appStats.dataSourceItemStats.lastProcessed}`
                  : undefined
              }
            />
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ flexGrow: 1, px: 0.5, pb: 1, minHeight: '400px' }}>
        <EntityLlmUsages entity={entity} />
      </Box>
    </Stack>
  );
};

export default UserAppUsageStats;
